/**
 * Device Service
 *
 * - 기기 목록 조회
 * - 기기-라이선스 매핑(RegisterDeviceLicense)
 */

import apiClient, { ApiError } from './apiClient';
import type {
    GetDeviceListResponseDto,
    GetOrganizationDeviceAllocationResultDto,
    DeviceAllocationProductRowDto,
    DeviceAllocationClassRowDto,
    DeviceListItemDto,
    RegisterDeviceLicenseRequestDto,
    RegisterDeviceLicenseResponseDto,
    UpdateDeviceLicenseRequestDto,
    UpdateDeviceLicenseResponseDto,
    GetDeviceTypeListResponseDto,
    RegisterDeviceRequestDto,
    RegisterDeviceResponseDto,
    UpdateDeviceRequestDto,
    UpdateDeviceResponseDto,
} from '../types/api/device';

function toPositiveInt(v: unknown): number | undefined {
	if (v == null || v === '') return undefined;
	const n = typeof v === 'number' ? v : Number(String(v).trim());
	if (!Number.isFinite(n) || n <= 0) return undefined;
	return Math.floor(n);
}

function toNonNegativeInt(v: unknown): number | undefined {
	if (v == null || v === '') return undefined;
	const n = typeof v === 'number' ? v : Number(String(v).trim());
	if (!Number.isFinite(n) || n < 0) return undefined;
	return Math.floor(n);
}

/**
 * API 문서 기준: GET /organization/:organizationId/device-allocation
 * Response: { products: Array<{ productId, productTitle, thumbnailImageUrl, allocatedDeviceCount, totalDeviceCount, classes: [...] }> }
 */
function parseDeviceAllocationProducts(
	products: unknown[],
	push: (classId: number, deviceId: number) => void
): DeviceAllocationProductRowDto[] {
	const productGroups: DeviceAllocationProductRowDto[] = [];
	
	for (const p of products) {
		if (p == null || typeof p !== 'object' || Array.isArray(p)) continue;
		const pr = p as Record<string, unknown>;
		
		const productId = toPositiveInt(pr.productId) ?? 0;
		const productTitle = typeof pr.productTitle === 'string' ? pr.productTitle : undefined;
		const thumbnailImageUrl = typeof pr.thumbnailImageUrl === 'string' ? pr.thumbnailImageUrl : null;
		const allocatedDeviceCount = toNonNegativeInt(pr.allocatedDeviceCount);
		const totalDeviceCount = toNonNegativeInt(pr.totalDeviceCount);

		const classesOut: DeviceAllocationClassRowDto[] = [];
		const classesArray = pr.classes;

		if (Array.isArray(classesArray)) {
			for (const c of classesArray) {
				if (c == null || typeof c !== 'object' || Array.isArray(c)) continue;
				const cr = c as Record<string, unknown>;
				
				const classId = toPositiveInt(cr.classId);
				if (classId == null) continue;
				
				const classTitle = typeof cr.classTitle === 'string' ? cr.classTitle : undefined;
				const devicesArray = cr.devices;
				const organizationDeviceIds: number[] = [];

				if (Array.isArray(devicesArray)) {
					for (const d of devicesArray) {
						if (d == null || typeof d !== 'object' || Array.isArray(d)) continue;
						const dr = d as Record<string, unknown>;
						const deviceId = toPositiveInt(dr.deviceId);
						if (deviceId != null) {
							organizationDeviceIds.push(deviceId);
							push(classId, deviceId);
						}
					}
				}

				classesOut.push({
					classId,
					classTitle,
					deviceSlotLimit: undefined,
					organizationDeviceIds,
				});
			}
		}

		productGroups.push({
			productId,
			productTitle,
			thumbnailImageUrl,
			allocatedDeviceCount,
			totalDeviceCount,
			classes: classesOut,
		});
	}
	
	return productGroups;
}

function coercePurchaseSource(v: unknown): string | null {
	if (v == null) return null;
	if (typeof v === 'string') return v;
	return null;
}

/**
 * API 문서 기준: GET /device/organization/list
 * Response devices 배열의 각 항목: { id, title, model, purchaseSource, createdAt, lastSigninAt, isActive }
 */
function normalizeDeviceListItem(rawRow: unknown): DeviceListItemDto {
	const row = (rawRow && typeof rawRow === 'object' ? rawRow : {}) as Record<string, unknown>;
	
	const id = toPositiveInt(row.id) ?? 0;
	const title = typeof row.title === 'string' ? row.title : '';
	const model = typeof row.model === 'string' ? row.model : '';
	const isActive = typeof row.isActive === 'boolean' ? row.isActive : true;
	const createdAt = typeof row.createdAt === 'string' ? row.createdAt : '';
	const lastSigninAt = row.lastSigninAt === null ? null : 
		typeof row.lastSigninAt === 'string' ? row.lastSigninAt : null;
	const purchaseSource = coercePurchaseSource(row.purchaseSource);

	return {
		id,
		title,
		model,
		isActive,
		createdAt,
		lastSigninAt,
		purchaseSource,
	};
}

/**
 * API 문서 기준: GET /device/organization/list
 * Response: { devices: Array<DeviceListItemDto>, total: number }
 */
export function normalizeOrganizationDeviceListResponse(raw: unknown): GetDeviceListResponseDto {
	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
		return { devices: [], total: 0 };
	}

	const response = raw as Record<string, unknown>;
	const devicesArray = response.devices;

	if (!Array.isArray(devicesArray)) {
		return { devices: [], total: 0 };
	}

	const devices: DeviceListItemDto[] = devicesArray.map(normalizeDeviceListItem);
	const total = typeof response.total === 'number' ? response.total : devices.length;
	return { devices, total };
}

/**
 * 기기 등록
 * POST /device/register
 */
export const registerDevice = async (data: RegisterDeviceRequestDto): Promise<RegisterDeviceResponseDto> => {
    try {
        return await apiClient.post<RegisterDeviceResponseDto>('/device/register', data);
    } catch (error) {
        if (error instanceof ApiError) throw new Error(error.message || '기기 등록에 실패했습니다.');
        throw error;
    }
};

/**
 * 기기 정보 변경
 * PUT /device/{organizationDeviceId}
 */
export const updateDevice = async (organizationDeviceId: number, data: UpdateDeviceRequestDto): Promise<UpdateDeviceResponseDto> => {
    try {
        return await apiClient.put<UpdateDeviceResponseDto>(`/device/${organizationDeviceId}`, data);
    } catch (error) {
        if (error instanceof ApiError) throw new Error(error.message || '기기 정보 변경에 실패했습니다.');
        throw error;
    }
};

/**
 * 기관 디바이스 삭제
 * DELETE /device/{organizationDeviceId}
 */
export const deleteDevice = async (organizationDeviceId: number): Promise<{ message: string }> => {
    try {
        return await apiClient.delete<{ message: string }>(`/device/${organizationDeviceId}`);
    } catch (error) {
        if (error instanceof ApiError) throw new Error(error.message || '기기 삭제에 실패했습니다.');
        throw error;
    }
};

/**
 * 기기 타입 목록 조회
 * GET /device/type/list
 */
export const getDeviceTypes = async (): Promise<GetDeviceTypeListResponseDto> => {
    try {
        /** 오픈클래스 목록 필터용 — 비로그인 접근 시에도 호출되므로 Authorization 없이 요청 */
        return await apiClient.get<GetDeviceTypeListResponseDto>('/device/type/list', {
            skipAuth: true,
        });
    } catch (error) {
        if (error instanceof ApiError) {
            throw new Error(error.message || '기기 타입 목록을 불러오는데 실패했습니다.');
        }
        throw error;
    }
};

/**
 * 특정 기관의 기기 목록 조회
 * GET /device/organization/list?organizationId=...
 * (백엔드 DTO에 `language` 쿼리 없음 — 전달 시 Nest VALIDATION_ERROR 400)
 */
export const getOrganizationDeviceList = async (organizationId: number): Promise<GetDeviceListResponseDto> => {
    try {
        const raw = await apiClient.get<unknown>('/device/organization/list', {
            params: { organizationId },
        });
        return normalizeOrganizationDeviceListResponse(raw);
    } catch (error) {
        if (error instanceof ApiError) {
            throw new Error(error.message || '기기 목록을 불러오는데 실패했습니다.');
        }
        throw error;
    }
};

/**
 * API 문서 기준: GET /organization/:organizationId/device-allocation
 * Response: { products: Array<{ productId, productTitle, ..., classes: Array<{ classId, ..., devices: [...] }> }> }
 */
function normalizeDeviceAllocationPayload(raw: unknown): GetOrganizationDeviceAllocationResultDto {
	const byClass: Record<number, number[]> = {};
	const push = (classId: number, deviceId: number) => {
		if (!byClass[classId]) byClass[classId] = [];
		byClass[classId].push(deviceId);
	};

	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
		return { byClassId: {} };
	}

	const response = raw as Record<string, unknown>;
	const productsArray = response.products;

	if (!Array.isArray(productsArray)) {
		return { byClassId: {} };
	}

	const productGroups = parseDeviceAllocationProducts(productsArray, push);

	for (const key of Object.keys(byClass)) {
		byClass[Number(key)] = [...new Set(byClass[Number(key)]!)];
	}

	return { byClassId: byClass, productGroups };
}

/**
 * 기관의 클래스—기기 할당 현황
 * `GET /organization/{organizationId}/device-allocation?language=...`
 */
export const getOrganizationDeviceAllocation = async (
	organizationId: number,
	options?: { language?: string }
): Promise<GetOrganizationDeviceAllocationResultDto> => {
	const oid = Math.floor(Number(organizationId));
	if (!Number.isFinite(oid) || oid <= 0) {
		return { byClassId: {} };
	}
	const path = `/organization/${oid}/device-allocation`;
	const language = (options?.language ?? '').trim();
	const params = language ? { language } : undefined;
	const raw = await apiClient.get<unknown>(path, params != null ? { params } : undefined);
	return normalizeDeviceAllocationPayload(raw);
};

/**
 * 기기-라이선스 매핑 등록
 * Swagger: POST /device/license/register (예측)
 */
export const registerDeviceLicense = async (
    data: RegisterDeviceLicenseRequestDto
): Promise<RegisterDeviceLicenseResponseDto> => {
    try {
        return await apiClient.post<RegisterDeviceLicenseResponseDto>('/device/license/register', data);
    } catch (error) {
        if (error instanceof ApiError) {
            throw new Error(error.message || '기기 라이선스 등록에 실패했습니다.');
        }
        throw error;
    }
};

/**
 * 기기-라이선스 매핑 수정
 * Swagger: PUT /device/license/update (예측)
 */
export const updateDeviceLicense = async (
    organizationLicenseId: number,
    data: UpdateDeviceLicenseRequestDto
): Promise<UpdateDeviceLicenseResponseDto> => {
    try {
        return await apiClient.put<UpdateDeviceLicenseResponseDto>(
            `/device/license/update?organizationLicenseId=${organizationLicenseId}`,
            data
        );
    } catch (error) {
        if (error instanceof ApiError) {
            throw new Error(error.message || '기기 라이선스 업데이트에 실패했습니다.');
        }
        throw error;
    }
};

/** alias: useDevice.ts 등에서 getDeviceTypeList로 참조 */
export const getDeviceTypeList = getDeviceTypes;
